import { useMemo } from 'react';

import { newestFirst } from '@/features/tasks/executionStatus';
import { useWorkspace } from '@/features/workspaces';
import { useMayReadUsage, useWorkspaceUsage } from '@/features/workspace-usage';
import type {
  AppliedLimitThreshold,
  TaskView,
  WorkspaceUsageSummary,
} from '@/lib/api/generated/model';
import {
  resolveLimitState,
  type LimitFigures,
  type LimitRefusals,
  type LimitState,
  type ResolvedLimits,
} from '../limitState';

/** Statuses a run reaches only after the spend limit let it through. */
const PAST_THE_LIMIT: ReadonlySet<string> = new Set([
  'provisioning',
  'submitting',
  'submitted',
  'running',
  'completed',
  'failed',
  'stopped',
  'awaiting_children',
]);

/**
 * Which scopes have actually refused a run, from data the board already holds.
 *
 * Only the newest run of each task counts. A refusal recorded on an older run
 * says nothing about now: the cap may since have been raised or the period
 * rolled. Ordering comes from the tasks feature's own helper so there is one
 * definition of "newest" in the product.
 *
 * Nor does a refusal some later run got past (spec 176). A task refused once
 * and never run again kept its refusal as its newest run, so the board said
 * "no runs will start" for weeks after the period reset, while other runs were
 * starting. A run that started after the refusal, anywhere in the workspace for
 * a workspace refusal and in the same workflow for a workflow one, proves the
 * limit no longer holds.
 */
function refusalsFrom(tasks: readonly TaskView[]): LimitRefusals {
  let startedInWorkspace = '';
  const startedInWorkflow = new Map<string, string>();
  for (const task of tasks) {
    for (const run of task.executions ?? []) {
      if (!PAST_THE_LIMIT.has(run.status)) continue;
      if (run.created_at > startedInWorkspace) {
        startedInWorkspace = run.created_at;
      }
      if (run.created_at > (startedInWorkflow.get(task.workflow_id) ?? '')) {
        startedInWorkflow.set(task.workflow_id, run.created_at);
      }
    }
  }

  let workspace = false;
  const workflows = new Set<string>();
  for (const task of tasks) {
    const [latest] = newestFirst(task.executions ?? []);
    if (!latest || latest.status !== 'blocked') continue;
    if (
      latest.blocked_scope === 'workspace' &&
      latest.created_at > startedInWorkspace
    ) {
      workspace = true;
    }
    if (
      latest.blocked_scope === 'workflow' &&
      latest.created_at > (startedInWorkflow.get(task.workflow_id) ?? '')
    ) {
      workflows.add(task.workflow_id);
    }
  }

  return { workspace, workflows };
}

/** The privileged reading, or null when it was refused, failed or has not answered. */
function figuresFrom(
  summary: WorkspaceUsageSummary | undefined,
): LimitFigures | null {
  if (!summary?.limit) return null;
  const byWorkflow = new Map<string, AppliedLimitThreshold>();
  for (const entry of summary.by_workflow ?? []) {
    // A workflow with no cap of its own has no state of its own; a null key is
    // the breakdown's "everything else" row and names no workflow.
    if (entry.key == null || entry.limit == null || entry.threshold == null) {
      continue;
    }
    byWorkflow.set(entry.key, entry.threshold);
  }

  return { workspace: summary.limit.threshold, byWorkflow };
}

/**
 * What is known about this workspace's limit, and this workflow's.
 *
 * Two sources sit behind this, and which one answered is part of the result
 * rather than something a caller has to work out (see `data-model.md`):
 *
 * - **A refused run**, on the workspace aggregate every member already reads to
 *   draw the board. It costs nothing and proves `exhausted`, but only after
 *   something has been refused.
 * - **The usage figures**, which say more and say it sooner, including the
 *   approaching state that exists nowhere else — and which the service serves
 *   only to an organisation administrator or the workspace's own. For everyone
 *   else the read is refused, and that is the ordinary case, not a fault.
 *
 * A refused or failed read becomes "not known". It is never surfaced as an
 * error, because it would then appear for most members of most workspaces, and
 * it is never read as "under the limit", because that is the one inference that
 * would claim a safety nobody established (spec 101 FR-032).
 */
function useLimitSources(workspaceId: string) {
  const workspace = useWorkspace(workspaceId);
  // Asked only of someone who may read the figures. For everyone else the read
  // is certain to be refused, and spending that round trip on every board load
  // to be told what identity already knows is a cost with no answer at the end
  // of it. The free path below is unaffected and works for every member.
  const mayRead = useMayReadUsage(workspaceId);
  // `undefined` is the current period, which is the only one a limit binds on.
  const usage = useWorkspaceUsage(workspaceId, undefined, { enabled: mayRead });

  const tasks = workspace.data?.tasks;
  const summary = usage.data;

  return useMemo(
    () => ({
      figures: figuresFrom(summary),
      refusals: refusalsFrom(tasks ?? []),
    }),
    [summary, tasks],
  );
}

export function useLimitState(
  workspaceId: string,
  workflowId?: string,
): ResolvedLimits {
  const sources = useLimitSources(workspaceId);

  return useMemo(
    () => resolveLimitState({ ...sources, workflowId }),
    [sources, workflowId],
  );
}

/**
 * The same answer for a list of workflows, read one at a time.
 *
 * A list renders a row per workflow, and a hook cannot be called inside that
 * loop. The sources are read once here and the resolution — which is pure — is
 * applied per row, so a sidebar of thirty workflows still makes exactly the two
 * reads one board makes.
 */
export function useLimitStates(workspaceId: string): {
  workspace: LimitState;
  forWorkflow: (workflowId: string) => LimitState;
} {
  const sources = useLimitSources(workspaceId);

  return useMemo(
    () => ({
      workspace: resolveLimitState({ ...sources, workflowId: undefined })
        .workspace,
      forWorkflow: (workflowId: string) =>
        resolveLimitState({ ...sources, workflowId }).workflow,
    }),
    [sources],
  );
}
